import { Box } from "../../../../../../mads";
import { useScrollYPosition } from "../../../../../../shared/util/scrollPosition";
import { useRnViewJobDisplay } from "../../../../context/ViewJobDisplayContext";

import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  Animated,
  Image,
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  type View,
  type LayoutRectangle,
  type ImageStyle,
  type LayoutChangeEvent,
  type StyleProp,
  Platform,
} from "react-native";

export type BannerProps = {
  /**
   * The URL of the banner IMG.
   */
  url: string;
  /**
   * The accessibility text of the img for screen readers.
   */
  altText: string;
};

type ElementDimensions = Pick<LayoutRectangle, "height" | "width">;

function useObserveDimensions(
  defaultDimensions: ElementDimensions = {
    width: 0,
    height: 0,
  },
): [ElementDimensions, React.RefCallback<HTMLDivElement>, (event: LayoutChangeEvent) => void] {
  const [dimensions, setDimensions] = useState<ElementDimensions>(defaultDimensions);

  const observedRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const frameRef = useRef<number | null>(null);

  const ref = useCallback(
    (element: HTMLDivElement | null) => {
      if (!globalThis.ResizeObserver) {
        return;
      }
      if (observerRef.current === null) {
        observerRef.current = new ResizeObserver(([entry]) => {
          if (!entry) {
            return;
          }
          let dims: ElementDimensions;
          if (entry.borderBoxSize) {
            const [{ inlineSize: width, blockSize: height }] = entry.borderBoxSize;
            dims = { width, height };
          } else {
            const { width, height } = entry.contentRect;
            dims = { width, height };
          }
          if (frameRef.current !== null) {
            cancelAnimationFrame(frameRef.current);
          }
          frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            setDimensions(dims);
          });
        });
      }

      // If the same element is passed again, do nothing.
      if (element === observedRef.current) {
        return;
      }

      // Detach from the previously observed element (if any).
      if (observedRef.current !== null) {
        observerRef.current.unobserve(observedRef.current);
      }

      observedRef.current = element;

      // Attach to the new element, or reset dimensions when detached.
      if (element !== null) {
        observerRef.current.observe(element);
      } else {
        if (frameRef.current !== null) {
          cancelAnimationFrame(frameRef.current);
          frameRef.current = null;
        }

        setDimensions(defaultDimensions);
      }
    },
    [defaultDimensions],
  );

  // callback that can be passed to native `onLayout`
  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setDimensions(event.nativeEvent.layout);
  }, []);

  return [dimensions, ref, handleLayout];
}

/**
 * Represents the Company Banner image that is present at the top of the page.
 */
export const Banner = (props: BannerProps) => {
  // Measure the parent's width instead of the full viewport.The banner is sometimes
  // rendered inside a narrower container (e.g. desktop 2pane), causing the image to
  // render absurdly tall relative to its actual rendered width.
  const [dimensions, dimensionRef, handleLayout] = useObserveDimensions(DEFAULT_BANNER_DIMENSIONS);
  const [image, setImage] = useState(DEFAULT_IMAGE_DETAILS);
  const scrollYPos = useScrollYPosition();

  // The scroll-driven parallax/fade is meaningful when the banner is pinned at the
  // top while page content scrolls past it (mobile fullscreen, desktop fullscreen).
  // In desktop 2pane embedded the banner scrolls together with the pane content,
  // and the ScrollYPosition provider (StickyButtonsRoot.web.tsx) is wired to
  // window.scrollY — i.e. the outer SERP scroll, not the pane scroll. That means
  // the interpolations read a stale window.scrollY value, fade the banner to
  // opacity 0, and never recover when the user scrolls the pane back to the top.
  // Disable the scroll-tied animation in this case.
  const disableScrollAnimation = useRnViewJobDisplay() === "DESKTOP_EMBEDDED";

  const imageHeight = Math.floor(dimensions.width * calculateImageRatio(image));

  const bannerStyle = useMemo(() => {
    if (disableScrollAnimation) {
      return {
        width: "100%",
        height: imageHeight,
      } satisfies StyleProp<ImageStyle>;
    }
    return {
      width: "100%",
      height: imageHeight,
      opacity: scrollYPos.interpolate({
        inputRange: [0, 2 * imageHeight],
        outputRange: [1, 0],
      }),
      transform: [
        {
          scale: scrollYPos.interpolate({
            inputRange: [-imageHeight, 0, imageHeight],
            outputRange: [2, 1, 1],
          }),
        },
        {
          translateY: scrollYPos.interpolate({
            inputRange: [-imageHeight, 0, imageHeight],
            outputRange: [-imageHeight * 0.25, 0, imageHeight * 0.2],
          }),
        },
      ],
    } satisfies StyleProp<ImageStyle>;
  }, [imageHeight, scrollYPos, disableScrollAnimation]);

  useEffect(() => {
    const abortController = new AbortController();
    Image.getSize(props.url, (srcImageWidth, srcImageHeight) => {
      if (!abortController.signal.aborted) {
        setImage({
          height: srcImageHeight,
          width: srcImageWidth,
        });
      }
    });

    return () => {
      abortController.abort();
    };
  }, [props.url]);

  // The wrapping Box is required so we can measure parent width via onLayout
  // before the Animated.Image mounts. Avoid mounting Animated.Image with
  // imageHeight=0 (happens before the first onLayout fires, during SSR, etc.)
  // because RNW's AnimatedProps freezes _inputStyle on first mount, so a zero
  // height would never recover — the banner would be permanently invisible
  // with a broken scale(2) transform.
  return (
    <Box
      ref={dimensionRef as React.RefCallback<View | null>}
      sx={styles.measureWrapper}
      onLayout={handleLayout}
      testID={"company-banner-wrapper"}
    >
      {imageHeight > 0 && (
        <Animated.Image
          testID={"company-banner"}
          source={{ uri: props.url }}
          style={bannerStyle}
          resizeMode={"cover"}
          accessibilityLabel={props.altText}
        />
      )}
    </Box>
  );
};

const styles = {
  measureWrapper: { width: "100%" as const },
};

type ImageDetails = {
  height: number;
  width: number;
};

// the banners seem to default to be 1960 x 400 so default to this ratio. If this is not the correct ratio
// there will be a slight flash as it switches the ratio. During that incorrect period it will not stretch the
// image though
export const DEFAULT_IMAGE_DETAILS: ImageDetails = {
  height: 400,
  width: 1960,
};

const DEFAULT_BANNER_DIMENSIONS = Platform.select({
  web: {
    width: 400,
    height: 80,
  },
  default: {
    width: 0,
    height: 0,
  },
});

export const calculateImageRatio = (image: ImageDetails): number => {
  return image.height / image.width;
};
